// Colors — one light theme matching the public website (imavenai.com, "Clear Blue").
// Source of truth: NewMainWebSite app/globals.css. Accents carry meaning there:
// blue = human advisors / primary actions, violet = AI, cyan = available now, amber = price.
export const colors = {
  // Website brand palette
  brand: {
    navy: '#17243e',
    ink: '#192947',
    blue: '#335cf4',
    indigo: '#5b61ea',
    violet: '#885de8',
    cyan: '#29a9dd',
    amber: '#ef9b61',
    gradient: 'linear-gradient(110deg, #2d5bed, #5b61ea)',
    gradientText: 'linear-gradient(103deg, #2865f2 6%, #6864f0 57%, #925ce9 98%)'
  },

  // Brand colors
  primary: {
    main: '#335cf4',
    light: '#5b7ff6',
    dark: '#2d5bed',
    hover: '#2d5bed',
    transparent: 'rgba(51, 92, 244, 0.1)'
  },
  secondary: {
    main: '#65748d',
    light: '#8190a7',
    dark: '#455776',
    hover: '#455776'
  },
  danger: {
    main: '#dc2f2f',
    light: '#e5484d',
    dark: '#b42323',
    hover: '#b42323'
  },
  success: {
    main: '#1f9d6b',
    light: '#22b07a',
    dark: '#0f7a55',
    hover: '#0f7a55'
  },
  warning: {
    main: '#ef9b61',
    light: '#f4b183',
    dark: '#d9822b',
    hover: '#d9822b'
  },

  // Blue-tinted greys (website ink scale)
  gray: {
    50: '#f8faff',
    100: '#f5f8fd',
    200: '#edf2ff',
    300: '#e0e8f5',
    400: '#cad5e9',
    500: '#8190a7',
    600: '#65748d',
    700: '#455776',
    800: '#33445e',
    900: '#192947'
  },

  // Text colors
  text: {
    primary: '#192947',
    secondary: '#65748d',
    disabled: '#a9b5c8'
  },

  // Semantic colors
  background: {
    default: '#f9fbff',
    paper: '#ffffff',
    disabled: '#f5f8fd',
    glass: '#ffffff',
    glassHover: '#f8faff'
  },

  // Border colors
  border: {
    default: '#e0e8f5',
    light: '#dce5f4',
    lightHover: '#b8c7e8'
  },

  // Status colors
  status: {
    online: '#1f9d6b',
    offline: '#dc2f2f',
    away: '#ef9b61'
  }
};

// Spacing
export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px'
};

// Typography
export const typography = {
  fontSize: {
    xs: '10px',
    sm: '12px', 
    md: '14px',
    lg: '16px',
    xl: '18px',
    xxl: '20px'
  },
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75
  }
};

// Border radius
export const borderRadius = {
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '10px',
  xxl: '12px',
  round: '50%'
};

// Shadows — soft, blue-tinted (website --shadow)
export const shadows = {
  sm: '0 1px 3px rgba(39, 68, 131, 0.06)',
  md: '0 4px 12px rgba(39, 68, 131, 0.07)',
  lg: '0 10px 30px rgba(39, 68, 131, 0.09)',
  card: {
    default: '0 3px 12px rgba(37, 65, 122, 0.04)',
    elevated: '0 11px 39px rgba(52, 77, 151, 0.085)',
    glass: '0 27px 100px rgba(39, 68, 131, 0.085), 0 5px 20px rgba(39, 68, 131, 0.035)',
    glassHover: '0 28px 100px rgba(42, 80, 171, 0.13), 0 5px 20px rgba(39, 68, 131, 0.05)'
  }
};

// Z-index
export const zIndex = {
  drawer: 1200,
  modal: 1300,
  popover: 1400,
  tooltip: 1500
};

// Transitions
export const transitions = {
  duration: {
    short: '0.15s',
    normal: '0.2s',
    long: '0.3s'
  },
  timing: 'ease-in-out'
};

// Component specific sizes
export const componentSizes = {
  iconButton: {
    sm: '32px',
    md: '40px',
    lg: '48px'
  },
  button: {
    padding: {
      sm: { x: '16px', y: '8px' },
      md: { x: '20px', y: '12px' },
      lg: { x: '24px', y: '16px' }
    },
    gap: '8px',
    loader: {
      size: '16px',
      borderWidth: '2px'
    }
  },
  card: {
    padding: {
      none: '0',
      sm: '12px',
      md: '16px',
      lg: '24px'
    }
  },
  input: {
    sm: '32px',
    md: '40px',
    lg: '48px'
  }
};

// Focus
export const focus = {
  ring: '0 0 0 4px rgba(85, 125, 246, 0.12)'
}; 